import {
  ApiGetInteractionEventPage,
  ApiPostInteractionEventReview,
} from "@/apis/interaction";
import { InteractionEventStatus } from "@/enums/mission";
import { InteractionEvent } from "@/types/entity";
import { useEventBus } from "@vueuse/core";
import merge from "lodash.merge";
import { defineStore } from "pinia";
import { showLoadingToast } from "vant";
import { useGlobalModal } from "./globalModal";
import { useNotification } from "./notification";
import { useGlobalUser } from "./globalUser";

const perPage = 10;

interface IList {
  ids: number[];
  pageNum: number;
}

export const useInteractiveReview = defineStore("interactive-review", {
  state: () => ({
    reivewed: {
      ids: [] as number[],
      pageNum: 0,
    } as IList,
    unreviewed: {
      ids: [] as number[],
      pageNum: 0,
    } as IList,
    all: {
      ids: [] as number[],
      pageNum: 0,
    } as IList,
    entities: {} as Record<number, InteractionEvent>,
    eventBus: useEventBus<string>("review-event"),
  }),
  getters: {
    reviewedList: (state) => state.reivewed.ids.map((id) => state.entities[id]),
    unreviewedList: (state) =>
      state.unreviewed.ids.map((id) => state.entities[id]),
    allList: (state): InteractionEvent[] =>
      state.all.ids.map((id) => state.entities[id]),
  },
  actions: {
    getReviewListObject(isReviewed: boolean | null) {
      if (isReviewed === null) return this.all;
      return this[isReviewed ? "reivewed" : "unreviewed"];
    },
    async initOrFetchList(
      isReviewed: boolean | null,
      force: boolean = false,
      mateId?: number
    ) {
      // 1. 获取缓存列表
      const cachedLists: IList = this.getReviewListObject(isReviewed);
      if (cachedLists.pageNum > 0 && !force) {
        return cachedLists;
      }
      // 2. 获取新列表
      const newPageNum = 1;
      const data = await this.fetchList(isReviewed, newPageNum, mateId);
      cachedLists.ids = data.map((item) => item.id);
      cachedLists.pageNum = newPageNum;
      this.eventBus.emit();
      useNotification().initSummaryCount();
      return cachedLists;
    },
    async fetchListNextPage(
      isReviewed: boolean | null,
      mateId?: number
    ): Promise<boolean> {
      const cachedLists: IList = this.getReviewListObject(isReviewed);
      const data = await this.fetchList(
        isReviewed,
        cachedLists.pageNum + 1,
        mateId
      );
      if (data.length === 0) {
        return false;
      }
      cachedLists.ids = cachedLists.ids.concat(data.map((item) => item.id));
      cachedLists.pageNum = cachedLists.pageNum + 1;
      return data.length >= perPage;
    },
    async fetchList(
      isReviewed: boolean | null,
      pageNum: number,
      mateId?: number,
      pageSize: number = perPage
    ) {
      let isReviewedFilter: 0 | 1 | undefined = undefined;
      if (isReviewed != null) {
        isReviewedFilter = isReviewed ? 1 : 0;
      }
      const { page } = await ApiGetInteractionEventPage({
        filter: {
          isReviewed: isReviewedFilter,
          mateId,
        },
        pagination: {
          page: pageNum,
          perPage: pageSize,
        },
        sort: {
          field: "id",
          order: "desc",
        },
      });
      return page.data.map((item) => this.updateEntity(item.id, item));
    },

    updateEntity(id: number, entity: InteractionEvent): InteractionEvent {
      // new => set, old => merge
      if (this.entities[id]) {
        merge(this.entities[id], entity);
      } else {
        this.entities[id] = entity;
      }
      return this.entities[id];
    },

    async reviewEvent(
      id: number,
      status: InteractionEventStatus.Approved | InteractionEventStatus.Rejected,
      powerCost: number = 0
    ) {
      const loading = showLoadingToast("");
      try {
        if (powerCost > 0) {
          const modalStore = useGlobalModal();
          if (
            modalStore.shouldOpenPowerNotEnoughModal({
              spentPowerAmount: powerCost,
              cb: () => this.reviewEvent(id, status, powerCost),
              plusModelRatio: true,
            })
          )
            return;
        }

        await ApiPostInteractionEventReview(id, {
          reviewStatus: status,
        });

        if (powerCost > 0) {
          useGlobalUser().refresh();
        }

        const notificationStore = useNotification();
        if (notificationStore.interactionSummary) {
          notificationStore.initSummaryCount();
        }

        this.unreviewed.ids = this.unreviewed.ids.filter(
          (oldId) => oldId !== id
        );
        this.reivewed.ids.unshift(id);
        await Promise.all([
          this.initOrFetchList(true, true),
          this.initOrFetchList(null, true),
        ]);
      } finally {
        loading.close();
      }
    },
  },
});
